import cx from "classnames";
import {
  Formik,
  useFormikContext,
  validateYupSchema,
  yupToFormErrors,
} from "formik";
import { Suspense, lazy, useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-grid-system";
import { boolean, object } from "yup";

import {
  Alert,
  Button,
  Card,
  Heading,
  Icon,
  Spinner,
} from "@icg360/design-system";

import {
  DataDefinition,
  DataTerm,
  InlineDataRow,
} from "components/common/data-row";
import { MainLayout } from "components/shared/main-layout";
import {
  useEligibleCoverages,
  useKeystone,
  usePolicyDetail,
  usePolicyPlatformId,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { type OptionalCoveragesQuery } from "gql/__generated__/hooks";
import {
  formatCurrency,
  formatDate,
  logException,
  scrollToTop,
  trackEvent,
} from "utils";

import { CoverageContext, useCoverages } from "../hooks";
import {
  type CoverageError,
  actionTypes,
  stepsReducer,
  useSteps,
} from "./hooks";
import styles from "./update-coverage.module.scss";

const CoverageCard = lazy(() => import("./coverage-card/index"));

export type CoverageUpdateFormSchema = {
  IDENTITY_FRAUD?: boolean;
  MECHANICAL_BREAKDOWN?: boolean;
  SERVICE_LINE?: boolean;
};

type CoverageUpdateFormProps = {
  setCoveragePage: (string) => void;
  step: string;
  error: CoverageError;
  dispatchError: (CoverageError) => void;
  coverages: OptionalCoveragesQuery["getEligibleCoverages"]["coverages"];
  setCoverageStep: () => void;
  setReviewStep: () => void;
};

const CoverageUpdateForm = ({
  setCoveragePage,
  step,
  error,
  dispatchError,
  coverages,
  setCoverageStep,
  setReviewStep,
}: CoverageUpdateFormProps) => {
  const { values, isSubmitting, setFieldValue, submitForm } =
    useFormikContext<CoverageUpdateFormSchema>();
  const [cart, setCart] = useState<Record<string, number>>({});
  const [date, setDate] = useState("");
  const [total, setTotal] = useState(0);
  const [leftLoading, setLeftLoading] = useState<number>(
    coverages?.length ?? 0
  );
  const {
    coveragePage: { y: yOffset },
  } = useContext(CoverageContext);

  // for some reason, this value is 0 at render and changes to the actual length later.
  useEffect(() => {
    setLeftLoading(coverages?.length ?? 0);
  }, [coverages]);

  useEffect(() => {
    const evalTotal = Object.values(cart).reduce((a, b) => a + b, 0);
    setTotal(evalTotal);

    if (error.set && evalTotal > 0) {
      dispatchError({ type: "unset" });
    }
  }, [cart, dispatchError, error.set]);

  const close = () => {
    setCoveragePage({ y: yOffset, policyId: "", type: "close" });
  };

  const next = () => {
    if (total > 0) {
      dispatchError({ type: "unset" });
      setReviewStep();
    } else {
      dispatchError({ type: "minimum" });
    }
  };

  let Main = (
    <>
      <CoveragesLoading />
      {/* we have to call each coverage so their prorated values can be calculated, individual cards will remain hidden until all have been loaded */}
      {coverages?.map((coverage, index) => (
        <CoverageCard
          dispatchError={dispatchError}
          step={step}
          coverage={coverage}
          setCart={setCart}
          setDate={setDate}
          leftLoading={leftLoading}
          setLeftLoading={setLeftLoading}
          key={index}
        />
      ))}
    </>
  );

  if (leftLoading <= 0) {
    Main = (
      <Card
        title="Which coverages would you like to add?"
        className={styles.sectionCard}
      >
        {error.set && (
          <Alert
            appearance="danger"
            title={error.title}
            description={error.description}
          />
        )}
        <div>
          <p className={styles.description}>
            Coverages you purchase are added to your policy and pro-rated for
            your current policy term.
          </p>
          <div className={styles.cardContainer}>
            {coverages?.map((coverage, index) => (
              <CoverageCard
                dispatchError={dispatchError}
                step={step}
                coverage={coverage}
                setCart={setCart}
                setDate={setDate}
                leftLoading={leftLoading}
                setLeftLoading={setLeftLoading}
                key={index}
              />
            ))}
          </div>
          <div className={styles.cardFooter}>
            <Button
              onPress={close}
              appearance="tertiary"
              data-testid="coverageGoBackButton"
            >
              Cancel
            </Button>

            <Button
              onPress={next}
              disabled={error.set && total <= 0}
              data-testid="reviewButton"
            >
              Review
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (step === actionTypes.review && leftLoading <= 0) {
    Main = (
      <Card title="Let’s review your purchase" className={styles.sectionCard}>
        {error.set && (
          <Alert
            appearance="danger"
            title={error.title}
            description={error.description}
          />
        )}
        <div>
          <p className={styles.description}>
            {`Your coverage will start on ${formatDate(
              date
            )} and will be added to your next billing statement.`}
          </p>

          <div className={styles.cardContainer}>
            {coverages?.map(
              (coverage, index) =>
                coverage?.name &&
                values[coverage.name] && (
                  <CoverageCard
                    dispatchError={dispatchError}
                    step={step}
                    coverage={coverage}
                    setCart={setCart}
                    setDate={setDate}
                    leftLoading={leftLoading}
                    setLeftLoading={setLeftLoading}
                    key={index}
                  />
                )
            )}

            <div className={styles.coverageTotal}>
              <InlineDataRow className={styles.proRate}>
                <DataTerm>Your pro-rated price</DataTerm>
                <DataDefinition className={styles.dataDefinition}>
                  {formatCurrency(total, true)}
                </DataDefinition>
              </InlineDataRow>
            </div>
          </div>

          <div className={styles.cardFooter}>
            <Button
              onPress={() => setCoverageStep()}
              appearance="tertiary"
              data-testid="reviewGoBackButton"
            >
              Go back
            </Button>

            <Button
              appearance="primary"
              size="default"
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              onPress={() => {
                setFieldValue("total", total);
                submitForm();
              }}
              data-testid="submitButton"
            >
              Complete purchase
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (error.set && error.type !== "minimum" && leftLoading <= 0) {
    Main = (
      <Card className={styles.sectionCard}>
        <div>
          <Alert
            appearance="danger"
            title={error.title}
            description={error.description}
            className={styles.error}
          />
          <div className={styles.cardFooter}>
            <Button
              onPress={() =>
                setCoveragePage({ y: 0, policyId: "", type: "close" })
              }
              appearance="tertiary"
              data-testid="errorCancelButton"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Suspense
      fallback={
        <MainLayout className={styles.maxWidth}>
          <CoveragesLoading />
        </MainLayout>
      }
    >
      <MainLayout className={styles.maxWidth}>{Main}</MainLayout>
    </Suspense>
  );
};

const CoverageHeader = ({ step }) => {
  return (
    <div className={styles.formHeaderContainer}>
      <Container className={styles.maxWidth}>
        <Row>
          <Col md={12} sm={12} xs={12}>
            <div className={styles.formHeader}>
              <Heading
                size="sm"
                className={cx(styles.step, {
                  [styles.active]: step === actionTypes.coverages,
                })}
              >
                Coverages
              </Heading>

              <Icon name="ArrowRight" size="sm" />

              <Heading
                size="sm"
                className={cx(styles.step, {
                  [styles.active]: step === actionTypes.review,
                })}
              >
                Review
              </Heading>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const CoveragesLoading = () => (
  <Card className={styles.sectionCard}>
    <div className={styles.loadingContainer}>
      <Spinner />
      <p>
        <b>We&apos;re checking your coverage prices. </b>
        This will take a few moments. Please do not close your browser or
        refresh the screen.
      </p>
    </div>
  </Card>
);

type CoverageUpdateProps = {
  error: CoverageError;
  dispatchError: (CoverageError) => void;
  setCoveragePage: (string) => void;
};

const CoverageUpdate = ({
  error,
  dispatchError,
  setCoveragePage,
}: CoverageUpdateProps) => {
  const keystone = useKeystone();
  const policyNum = usePolicyPlatformId();
  const { mutate: refetchPolicy } = usePolicyDetail();
  const { data, isLoading } = useEligibleCoverages();

  useEffect(() => {
    scrollToTop();
    dispatchError({ type: "unset" });
  }, [dispatchError]);

  useEffect(() => {
    if (error.set) {
      scrollToTop();
    }
  }, [error]);

  const coveragesToShow = useCoverages(data?.coverages);
  const { step, setCoverageStep, setReviewStep } = useSteps({
    reducer: stepsReducer,
  });

  if (isLoading || !coveragesToShow) {
    return null;
  }

  // map each coverage value to false and return that array
  // if we don't initialize this state, react will throw a warning when we use them in a checkbox
  const initialValues = Object.values(data?.coverages ?? []).reduce(
    (o, coverage) => ({ ...o, [coverage?.name ?? ""]: false }),
    {}
  );

  const validate = async (values) => {
    const coveragesSchemaResolver = Object.values(data?.coverages ?? []).reduce(
      (o, coverage) => ({ ...o, [coverage?.name ?? ""]: boolean() }),
      {}
    );
    const coveragesSchema = object().shape(coveragesSchemaResolver);

    try {
      await validateYupSchema(values, coveragesSchema, true);
    } catch (error) {
      return yupToFormErrors(error);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const { ...coverages } = values;

    async function purchase(eligibleCoverages, coverages) {
      setSubmitting(true);
      const successful: string[] = [];
      const failed: string[] = [];
      for (const coverage of eligibleCoverages) {
        if (coverages[coverage.name]) {
          const res = await keystone.purchaseCoverage({
            policyNum: policyNum ?? "",
            coverageName: coverage.name,
            protectionValue: coverage.protectionValue,
          });

          if (isError(res)) {
            trackEvent("Additional coverages error -- Purchasing failed", {
              coverage: coverage.name,
              policyId: policyNum,
            });
            failed.push(coverage.name);
            return { successful, failed };
          }
          trackEvent("Additional coverages -- Purchasing succeeded", {
            coverage: coverage.name,
            policyId: policyNum,
          });
          successful.push(coverage.name);
        }
      }

      return { successful, failed };
    }

    try {
      setSubmitting(true);
      const { coverages: eligibleCoverages } = data ?? {};

      const { successful, failed } = await purchase(
        eligibleCoverages,
        coverages
      );
      setSubmitting(false);

      if (failed.length > 0) {
        const numCovered = failed.length + successful.length;

        if (numCovered === failed.length) {
          setCoveragePage({ policyId: policyNum, type: "danger" });
        } else {
          setCoveragePage({
            policyId: policyNum,
            type: "warning",
            failed,
          });
        }

        throw new Error(
          `succeeded: ${successful.join(" ")}, failed: ${failed.join(" ")}`
        );
      } else {
        setCoveragePage({ policyId: policyNum, type: "success" });
      }
      refetchPolicy();
    } catch (err) {
      logException(err);
      dispatchError({});
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <CoverageHeader step={step} />

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        enableReinitialize
      >
        <CoverageUpdateForm
          setCoveragePage={setCoveragePage}
          step={step}
          setCoverageStep={setCoverageStep}
          setReviewStep={setReviewStep}
          coverages={coveragesToShow}
          error={error}
          dispatchError={dispatchError}
        />
      </Formik>
    </div>
  );
};

export default CoverageUpdate;
